import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate, useOutletContext, useParams } from 'react-router-dom';

import useCoupons from 'api/useCoupons';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

import EmptyState from 'components/EmptyState/EmptyState';
import { TableView } from 'components/TableView';

import { EToasterStatus, showToast } from '../../utils/showToast';

import { COUPONS_TABLE_COLUMNS } from './constants/coupons';
import { CouponsOutletContext } from './types';
import { isDatePassed } from './utils';

dayjs.extend(isSameOrAfter);

const CouponsTable = () => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const {
    couponNameValue,
    promoCodeValue,
    discountValue,
    statusValues,
    rangeDates,
    isDisabledDueToPermissions
  } = useOutletContext<CouponsOutletContext>();

  const { getCoupons, setCouponActive } = useCoupons(publisherId);
  const [searchedRows, setSearchedRows] = useState<any[]>([]);

  const toggleActivateCoupon = useCallback(
    async (couponId: string, active: boolean) => {
      setCouponActive.mutate(
        {
          couponId,
          active
        },
        {
          onSuccess: () => {
            showToast({
              message: `Coupon ${active ? 'activated' : 'deactivated'} successfully`,
              status: EToasterStatus.SUCCESS
            });
            getCoupons.refetch();
          },
          onError: (error: any) => {
            showToast({
              message: `Failed to ${active ? 'activate' : 'deactivate'} coupon`,
              status: EToasterStatus.ERROR,
              description: error.message || ''
            });
          }
        }
      );
    },
    [setCouponActive, getCoupons]
  );

  const isCouponExpired = useCallback((expiredBy: string) => {
    if (!expiredBy) {
      return false;
    }
    return isDatePassed(expiredBy);
  }, []);

  const columns = useMemo(() => {
    return COUPONS_TABLE_COLUMNS({
      navigate,
      toggleActivateCoupon,
      isDisabledDueToPermissions,
      isCouponExpired
    });
  }, [
    navigate,
    toggleActivateCoupon,
    isDisabledDueToPermissions,
    isCouponExpired
  ]);

  useEffect(() => {
    let returnedRows = getCoupons?.data || [];

    if (couponNameValue !== '') {
      returnedRows = returnedRows.filter((row) =>
        row.name.toLowerCase().includes(couponNameValue.toLowerCase())
      );
    }

    if (promoCodeValue !== '') {
      returnedRows = returnedRows.filter((row) =>
        row.promoCodeNames?.find((promoCodeName: string) =>
          promoCodeName.toLowerCase().includes(promoCodeValue.toLowerCase())
        )
      );
    }
    if (discountValue !== '') {
      returnedRows = returnedRows.filter((row: any) => {
        return row?.discountPercentage === +discountValue;
      });
    }
    if (statusValues.length > 0) {
      returnedRows = returnedRows.filter((row: any) => {
        const filterActive = statusValues.includes('active');
        const filterInactiveActive = statusValues.includes('inactive');
        const isExpired = isCouponExpired(row.expiredBy);
        if (filterActive && filterInactiveActive) {
          return row;
        } else if (filterActive) {
          return !!row.active && !isExpired;
        }
        return !row.active || isExpired;
      });
    }
    if (rangeDates?.length) {
      const [from, to] = rangeDates;
      returnedRows = returnedRows.filter((row: any) => {
        const createdAt = row.createdAt;
        const isAfterStartDay = dayjs(createdAt).isSameOrAfter(from, 'day');
        const isBeforeEndDay =
          dayjs(createdAt).isBefore(to, 'day') ||
          dayjs(createdAt).isSame(to, 'day');
        return isAfterStartDay && isBeforeEndDay;
      });
    }
    setSearchedRows(returnedRows);
  }, [
    couponNameValue,
    promoCodeValue,
    discountValue,
    statusValues,
    getCoupons?.data,
    rangeDates,
    isCouponExpired
  ]);

  const isFilteredEmpty =
    searchedRows.length === 0 && !!getCoupons?.data?.length;

  if (isFilteredEmpty) {
    return (
      <div className="flex items-center justify-center w-full min-h-[calc(100vh-350px)]">
        <EmptyState
          title="No exact matches"
          description="Try changing or removing some of your filters or adjusting your search area."
          icon="fa-regular fa-filter"
          hideButton
        />
      </div>
    );
  }

  return (
    <TableView
      title="Coupons List"
      data={searchedRows}
      columns={columns}
      isLoading={getCoupons.isLoading || getCoupons.isFetching}
      containerClassName="min-h-0"
      wrapperClassName="border-b"
    />
  );
};

export default CouponsTable;
